/**
 * Shared DOM/text helpers for rendering untrusted shop data into the map popups and the sidebar.
 * Server data is treated as untrusted at the boundary (OSM tags are world-editable), so these are the
 * single escaping + URL-validation + Maps-link routines both ``map.ts`` and ``sidebar.ts`` use — kept
 * in one place so the (security-sensitive) logic cannot drift between the two renderers.
 */

export function escapeHtml(value: string): string {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

/**
 * The public ``website`` value comes from the world-editable OSM ``website``/``contact:website`` tag,
 * so it is untrusted. Allow it as a link only when it parses as an absolute http(s) URL — this rejects
 * ``javascript:``/``data:``/``vbscript:`` scheme XSS that escapeHtml (which neither validates schemes
 * nor escapes quotes for an attribute context) would let through. Returns the normalised href or null.
 */
export function safeHttpUrl(value: string): string | null {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : null;
  } catch {
    return null;
  }
}

/**
 * Google Maps link for a PARTNER shop, biased to open the REAL business (verified against live
 * Google Maps): the place page (/maps/place/<name>/@lat,lng) — the business card. A nameless row
 * degrades to a coordinate pin. Places LEADS never come through here: they carry their own
 * `maps_url` deep-link (query_place_id) straight from the backend.
 */
export function googleMapsHref(name: string, lng: number, lat: number): string {
  if (!name.trim()) {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  }
  return `https://www.google.com/maps/place/${encodeURIComponent(name)}/@${lat},${lng},18z`;
}
